import { v4 as uuidv4 } from "uuid";
import { makeAutoObservable, reaction } from "mobx";
import Workout from "./Workout";
import Exercise from "./Exercise";
import Serie from "./Serie";
import Attribute from "./Attribute";
import { ProtocolTitles } from "../../Resources/Constants";
import {
  endOfWeek,
  getMondayOfWeek,
  getSundayOfWeek,
  startOfWeek,
} from "../utils/dateUtils";
import { validateBlock } from "../utils/firestoreValidation";
import { showDevToast } from "../utils/devToast";

class Block {
  blockViewEditingWorkoutId = null;

  weekToDelete = null;

  isProtocolAttributesShowing = false;

  //BLOCK PROTOCOLS
  splitSelectedWeek = 1;
  protocols = [];
  showingInfoView = false;

  selectedSlotId = null;

  selectedProtocolSlot = null;
  selectedProtocolWeek = null;

  weekNames = {};
  hiddenWeeks = [];

  showingOverviewStats = false;

  protocolSeriesShown = [];

  showingDashboardActual = false;

  //LINK EXERCISE WITH SLOT
  selectedLift = null;
  selectedPriority = null;
  selectedSecondary = false;

  //CALENDAR
  calendarStartDate = null;

  //WORKOUT POSITION FILTER (session-only, resets on refresh)
  selectedWorkoutPositions = null; // null = all positions, Set([1,2]) = only positions 1 and 2

  //EXERCISE EXPAND/COLLAPSE STATE (session-only, resets on refresh)
  exercisesExpanded = true; // true = expanded (show sets), false = collapsed (hide sets)

  constructor(
    id = uuidv4(),
    name = "",
    notes = "",
    workouts = [],
    isSelected = false,
    isShown = false,
    client = null,
    selectedWeek = 1,
    totalWeeks = 6,
    label = "",
    last_update = new Date(),
    coach = null,
    order = null,
  ) {
    this.id = id;
    this.name = name;
    this.notes = notes;
    this.workouts = workouts;
    this.isSelected = isSelected;
    this.isShown = isShown;
    this.client = client;
    this.selectedWeek = selectedWeek;
    this.totalWeeks = totalWeeks;
    this.label = label;
    this.lastUpdate = last_update;
    this.coach = coach;
    this.order = Number.isFinite(Number(order)) ? Number(order) : null;
    this.isShowingDashboard = false;

    if (this.coach) {
      this.isShowingDashboard =
        this.coach.defaultBlockView === "dashboard-string";
    } else if (this.client) {
      this.isShowingDashboard =
        this.client.coach.defaultBlockView === "dashboard-string";
    }
    makeAutoObservable(this);

    reaction(
      () => ({
        name: this.name,
        notes: this.notes,
        total_weeks: this.totalWeeks,
        label: this.label,
        workouts: this.workouts.length,
        hiddenWeeks: this.hiddenWeeks.length,
        calendarStartDate: this.calendarStartDate,
      }),
      () => {
        if (this.client?.avoidTransaction === false) {
          this.client.updateClientTransactionData(this.id.toLowerCase());
          this.client.startOrResetBatchTimer();
        }

        if (this.coach && !this.coach.isFetching) {
          this.coach.updateCoachTransactionBlocks(this.id.toLowerCase());
          this.coach.startOrResetBatchTimer();
        }
      },
    );
    reaction(
      () => ({
        weeks: this.getWeeks,
      }),
      () => {
        if (this.getWeeks > this.totalWeeks) {
          this.totalWeeks = this.getWeeks;
        }
      },
    );
  }

  // #region /* REAL-TIME & COPY */
  updateBlockRealTime(blockData) {
    const hasBlockLocalChanges = this.client.clientTransactionData.includes(
      this.id.toString(),
    );

    const firestoreWorkouts = blockData.workouts || [];

    /* ELIMINAR WORKOUTS */
    const firestoreWorkoutIds = new Set(
      firestoreWorkouts.map((workout) => workout.id.toLowerCase()),
    );
    const localWorkoutIds = new Set(
      this.workouts.map((workout) => workout.id.toString().toLowerCase()),
    );
    const workoutsToDeleteIds = new Set(
      [...localWorkoutIds].filter((id) => !firestoreWorkoutIds.has(id)),
    );
    workoutsToDeleteIds.forEach((workoutId) => {
      const index = this.workouts.findIndex(
        (s) => s.id === workoutId && s.hasChanged === false,
      );
      if (index > -1) {
        this.workouts.splice(index, 1);
      }
    });

    const workoutsToUpdate =
      this.lastUpdate === null
        ? firestoreWorkouts
        : firestoreWorkouts.filter((workout) => {
            if (!workout?.last_update) return false;
            const workoutLastUpdate = workout.last_update.toDate();
            if (!workoutLastUpdate || isNaN(workoutLastUpdate.getTime()))
              return false;
            return workoutLastUpdate.getTime() > this.lastUpdate.getTime();
          });

    const workoutsToUpdateString = workoutsToUpdate.map((w) => w.name);

    console.log(
      "Change done from another Web Window. Updating these workouts:",
      workoutsToUpdateString,
    );

    workoutsToUpdate.forEach((firestoreWorkout) => {
      const internalWorkout = this.workouts.find(
        (w) => w.id.toString() === firestoreWorkout.id,
      );

      if (internalWorkout) {
        const hasWorkoutLocalChanges = internalWorkout.hasChanged;
        if (!hasWorkoutLocalChanges) {
          internalWorkout.updateWorkout(firestoreWorkout);
        }
        const firestoreExercises = firestoreWorkout.exercises || [];

        if (!hasWorkoutLocalChanges) {
          /* ELIMINAR EXERCICIS */
          const firestoreExerciseIDs = new Set(
            firestoreExercises.map((e) => e.id),
          );
          const localExerciseIDs = new Set(
            internalWorkout.exercises.map((e) => e.id.toString()),
          );
          const deletedExerciseIDs = [...localExerciseIDs].filter(
            (id) => !firestoreExerciseIDs.has(id),
          );
          deletedExerciseIDs.forEach((exerciseId) => {
            const index = internalWorkout.exercises.findIndex(
              (s) => s.id === exerciseId,
            );
            if (index > -1) {
              internalWorkout.exercises.splice(index, 1);
            }
          });
        }
        firestoreExercises.forEach((firestoreExercise) => {
          const internalExercise = internalWorkout.exercises.find(
            (e) => e.id.toString() === firestoreExercise.id,
          );
          if (internalExercise) {
            !hasWorkoutLocalChanges &&
              internalExercise.updateExercise(firestoreExercise);

            const firestoreSeries = firestoreExercise.series || [];

            if (!hasWorkoutLocalChanges) {
              /* ELIMINAR SERIES */
              const firestoreSeriesIDs = new Set(
                firestoreSeries.map((s) => s.id),
              );
              const localSeriesIDs = new Set(
                internalExercise.series.map((s) => s.id.toString()),
              );
              const deletedSeriesIDs = [...localSeriesIDs].filter(
                (id) => !firestoreSeriesIDs.has(id),
              );
              deletedSeriesIDs.forEach((serieId) => {
                const index = internalExercise.series.findIndex(
                  (s) => s.id === serieId,
                );
                if (index > -1) {
                  internalExercise.series.splice(index, 1);
                }
              });
            }
            firestoreSeries.forEach((firestoreSerie) => {
              const internalSerie = internalExercise.series.find(
                (s) => s.id.toString() === firestoreSerie.id,
              );
              if (internalSerie) {
                internalSerie.updateSerie(
                  firestoreSerie,
                  hasWorkoutLocalChanges,
                );
              } else {
                !hasWorkoutLocalChanges &&
                  internalExercise.fetchSerie(firestoreSerie);
              }
            });

            // Re-sort series array by order after all updates to ensure correct display order
            internalExercise.series.sort(
              (a, b) => (a?.order ?? 0) - (b?.order ?? 0),
            );

            const firestoreAttributes = firestoreExercise.attributes || [];

            if (!hasWorkoutLocalChanges) {
              /* ELIMINAR ATTRIBUTES */
              const firestoreAttributeIDs = new Set(
                firestoreAttributes.map((a) => a.id),
              );
              const localAttributeIDs = new Set(
                internalExercise.attributes.map((a) => a.id.toString()),
              );
              const deletedAttributeIDs = [...localAttributeIDs].filter(
                (id) => !firestoreAttributeIDs.has(id),
              );
              deletedAttributeIDs.forEach((attributeId) => {
                const index = internalExercise.attributes.findIndex(
                  (s) => s.id === attributeId,
                );
                if (index > -1) {
                  internalExercise.attributes.splice(index, 1);
                }
              });
              firestoreAttributes.forEach((firestoreAttribute) => {
                const internalAttribute = internalExercise.attributes.find(
                  (a) => a.id.toString() === firestoreAttribute.id,
                );
                if (internalAttribute) {
                  internalAttribute.updateAttribute(firestoreAttribute);
                } else {
                  internalExercise.fetchAttribute(firestoreAttribute);
                }
              });
            }
          } else {
            !hasWorkoutLocalChanges &&
              internalWorkout.fetchExercise(firestoreExercise);
          }
        });
      } else {
        !hasBlockLocalChanges && this.fetchWorkout(firestoreWorkout);
      }
    });
    if (!hasBlockLocalChanges) {
      this.totalWeeks = blockData.total_weeks ?? 6;
      this.label = blockData.label ?? "";
      this.notes = blockData.notes ?? "";
      this.name = blockData.name ?? "";
      this.order = Number.isFinite(Number(blockData.order))
        ? Number(blockData.order)
        : this.order ?? null;

      this.hiddenWeeks = blockData.hidden_weeks ?? [];
      this.weekNames = blockData.week_names ?? {};
      this.calendarStartDate = blockData.calendar_start_date?.toDate() ?? null;
    }

    //UpdateProtocols in real-time
    if (blockData.protocols && !hasBlockLocalChanges) {
      console.log("doing fetchBlockProtocolsRealTime");
      this.fetchBlockProtocolsRealTime(blockData.protocols);
    }
    this.lastUpdate = blockData?.last_update?.toDate() || new Date();
  }

  copyWorkout(workout, newWeek = workout.week, newDate = null) {
    const newWorkout = new Workout(
      undefined,
      newDate,
      workout.name,
      workout.order,
      newWeek,
      false,
      this,
      [],
    );
    newWorkout.setProperty("hasChanged", true);

    workout.exercises.forEach((exercise) => {
      newWorkout.copyExercise(exercise);
    });

    this.workouts.push(newWorkout);
  }

  fetchWorkout(workout) {
    const newWorkout = new Workout(
      String(workout.id),
      workout.date ? workout.date.toDate() : null,
      workout.name,
      workout.order,
      workout.week,
      false,
      this,
      [],
    );

    workout.exercises.forEach((exercise) => {
      newWorkout.fetchExercise(exercise);
    });

    this.workouts.push(newWorkout);
  }

  fetchSlotExercise(exercise) {
    const newExercise = new Exercise(
      String(exercise.id),
      exercise.name,
      exercise.original_name,
      exercise.notes,
      exercise.order,
      undefined,
      false,
      undefined,
      [],
      [],
      undefined,
      this,
      exercise.link,
    );

    newExercise.isSecondaryProtocol = exercise.is_secondary_protocol ?? false;

    newExercise.protocolSlot = exercise.protocol_slot ?? null;
    newExercise.protocolWeek = exercise.protocol_week ?? null;
    newExercise.protocolText = exercise.protocol_text ?? "";

    exercise?.series.forEach((serie) => {
      newExercise.fetchSerie(serie);
    });
    exercise?.attributes.forEach((attribute) => {
      newExercise.fetchAttribute(attribute);
    });

    this.protocols.push(newExercise);
  }

  copySlotExercise(exercise) {
    const newExercise = new Exercise(
      undefined,
      exercise.name,
      exercise.originalName,
      exercise.notes,
      exercise.order,
      undefined,
      false,
      undefined,
      [],
      [],
      undefined,
      this,
      exercise.link,
    );

    newExercise.isSecondaryProtocol = exercise.isSecondaryProtocol ?? false;

    newExercise.protocolSlot = exercise.protocolSlot ?? null;
    newExercise.protocolWeek = exercise.protocolWeek ?? null;
    newExercise.protocolText = exercise.protocolText ?? "";

    exercise?.series.forEach((serie) => {
      newExercise.copySerie(serie);
    });
    exercise?.attributes.forEach((attribute) => {
      newExercise.copyAttribute(attribute);
    });

    this.protocols.push(newExercise);
  }

  uploadToCloud(timestamp = null) {
    const workoutsArray = this.workouts.map((workout) =>
      workout.uploadToCloud(timestamp),
    );

    let protocols = this.protocols.map((exercise) => {
      return exercise.uploadToCloud();
    });

    const data = {
      id: this.id.toString().toLowerCase(),
      name: this.name,
      notes: this.notes,
      weeks: this.getWeeks,
      total_weeks: this.totalWeeks,
      label: this.label,
      workouts: workoutsArray,
      ...(this.protocols.length > 0 ? { protocols: protocols } : {}),
      ...(this.hiddenWeeks.length > 0
        ? { hidden_weeks: this.hiddenWeeks }
        : {}),
      ...(Object.keys(this.weekNames || {}).length > 0
        ? { week_names: this.weekNames }
        : {}),
      modified_by: this.client.coach.sessionId.toString().toLowerCase(),
      ...(this.calendarStartDate
        ? { calendar_start_date: this.calendarStartDate }
        : {}),
    };
    if (timestamp) {
      data.last_update = timestamp;
    }

    // Validate data (always runs, lightweight)
    // Only show toast in development
    if (process.env.NODE_ENV === "development") {
      const validation = validateBlock(data);
      if (!validation.valid) {
        const errorMsg = validation.errors.join("; ");
        showDevToast("Block validation failed", errorMsg);
      }
    }

    return data;
  }

  fetchBlockProtocolsRealTime(firestoreExercises = []) {
    /* ELIMINAR EXERCICIS */
    const firestoreExerciseIDs = new Set(firestoreExercises.map((e) => e.id));
    const localExerciseIDs = new Set(
      this.protocols.map((e) => e.id.toString()),
    );
    const deletedExerciseIDs = [...localExerciseIDs].filter(
      (id) => !firestoreExerciseIDs.has(id),
    );
    deletedExerciseIDs.forEach((exerciseId) => {
      const index = this.protocols.findIndex((s) => s.id === exerciseId);
      if (index > -1) {
        this.protocols.splice(index, 1);
      }
    });

    firestoreExercises.forEach((firestoreExercise) => {
      const internalExercise = this.protocols.find(
        (e) => e.id === firestoreExercise.id,
      );

      if (internalExercise) {
        internalExercise.updateExercise(firestoreExercise);

        const firestoreSeries = firestoreExercise.series || [];

        /* ELIMINAR SERIES */
        const firestoreSeriesIDs = new Set(firestoreSeries.map((s) => s.id));
        const localSeriesIDs = new Set(
          internalExercise.series.map((s) => s.id.toString()),
        );
        const deletedSeriesIDs = [...localSeriesIDs].filter(
          (id) => !firestoreSeriesIDs.has(id),
        );
        deletedSeriesIDs.forEach((serieId) => {
          const index = internalExercise.series.findIndex(
            (s) => s.id === serieId,
          );
          if (index > -1) {
            internalExercise.series.splice(index, 1);
          }
        });

        firestoreSeries.forEach((firestoreSerie) => {
          const internalSerie = internalExercise.series.find(
            (s) => s.id.toString() === firestoreSerie.id,
          );
          if (internalSerie) {
            internalSerie.updateSerie(firestoreSerie, false);
          } else {
            internalExercise.fetchSerie(firestoreSerie);
          }
        });

        const firestoreAttributes = firestoreExercise.attributes || [];

        /* ELIMINAR ATTRIBUTES */
        const firestoreAttributeIDs = new Set(
          firestoreAttributes.map((a) => a.id),
        );
        const localAttributeIDs = new Set(
          internalExercise.attributes.map((a) => a.id.toString()),
        );
        const deletedAttributeIDs = [...localAttributeIDs].filter(
          (id) => !firestoreAttributeIDs.has(id),
        );
        deletedAttributeIDs.forEach((attributeId) => {
          const index = internalExercise.attributes.findIndex(
            (s) => s.id === attributeId,
          );
          if (index > -1) {
            internalExercise.attributes.splice(index, 1);
          }
        });
        firestoreAttributes.forEach((firestoreAttribute) => {
          const internalAttribute = internalExercise.attributes.find(
            (a) => a.id.toString() === firestoreAttribute.id,
          );
          if (internalAttribute) {
            internalAttribute.updateAttribute(firestoreAttribute);
          } else {
            internalExercise.fetchAttribute(firestoreAttribute);
          }
        });
      } else {
        this.fetchSlotExercise(firestoreExercise);
      }
    });
  }

  fetchBlockProtocols(protocolsData) {
    protocolsData?.forEach((exerciseData) => {
      this.fetchSlotExercise(exerciseData);
    });
  }

  updateOldProtocols(
    slotData,
    slotType,
    protocolsArray,
    secondaryProtocolsArray,
    blockName,
  ) {
    if (slotData) {
      let migrationNeeded = false;

      slotData.forEach((oldSlot) => {
        const newExercise = new Exercise(
          oldSlot.id,
          oldSlot.name,
          oldSlot.original_name,
          oldSlot.notes,
          oldSlot.order,
          undefined,
          false,
          null,
          [],
          [],
          undefined,
          this,
        );

        if (slotType === "squat_slots") {
          if (oldSlot.order === 0) {
            newExercise.protocolSlot = ProtocolTitles.PRIMARY_SQUAT;
          } else if (oldSlot.order === 1) {
            newExercise.protocolSlot = ProtocolTitles.SECONDARY_SQUAT;
          } else if (oldSlot.order === 2) {
            newExercise.protocolSlot = ProtocolTitles.TERTIARY_SQUAT;
          } else if (oldSlot.order === 3) {
            newExercise.protocolSlot = ProtocolTitles.QUATERNARY_SQUAT;
          } else if (oldSlot.order === 4) {
            newExercise.protocolSlot = ProtocolTitles.QUINARY_SQUAT;
          }
        } else if (slotType === "bench_press_slots") {
          if (oldSlot.order === 0) {
            newExercise.protocolSlot = ProtocolTitles.PRIMARY_BENCH_PRESS;
          } else if (oldSlot.order === 1) {
            newExercise.protocolSlot = ProtocolTitles.SECONDARY_BENCH_PRESS;
          } else if (oldSlot.order === 2) {
            newExercise.protocolSlot = ProtocolTitles.TERTIARY_BENCH_PRESS;
          } else if (oldSlot.order === 3) {
            newExercise.protocolSlot = ProtocolTitles.QUATERNARY_BENCH_PRESS;
          } else if (oldSlot.order === 4) {
            newExercise.protocolSlot = ProtocolTitles.QUINARY_BENCH_PRESS;
          }
        } else if (slotType === "deadlift_slots") {
          if (oldSlot.order === 0) {
            newExercise.protocolSlot = ProtocolTitles.PRIMARY_DEADLIFT;
          } else if (oldSlot.order === 1) {
            newExercise.protocolSlot = ProtocolTitles.SECONDARY_DEADLIFT;
          } else if (oldSlot.order === 2) {
            newExercise.protocolSlot = ProtocolTitles.TERTIARY_DEADLIFT;
          } else if (oldSlot.order === 3) {
            newExercise.protocolSlot = ProtocolTitles.QUATERNARY_DEADLIFT;
          } else if (oldSlot.order === 4) {
            newExercise.protocolSlot = ProtocolTitles.QUINARY_DEADLIFT;
          }
        }

        newExercise.protocolWeek = 1;

        if (protocolsArray && protocolsArray[oldSlot.order]) {
          newExercise.protocolText = protocolsArray[oldSlot.order];

          const firstWeekWorkouts = this.workouts.filter((w) => w.week === 1);

          const allFirstWeekExercises = firstWeekWorkouts.flatMap(
            (w) => w.exercises,
          );

          const matchingExercises = allFirstWeekExercises.filter(
            (ex) => ex.protocolSlot === newExercise.protocolSlot,
          );

          if (matchingExercises.length > 0) {
            matchingExercises.forEach((ex) => {
              if (protocolsArray[oldSlot.order]) {
                // console.log(
                //   `Assigning protocolText "${
                //     protocolsArray[oldSlot.order]
                //   }" to exercise:`,
                //   ex.name
                // );
                ex.protocolText = protocolsArray[oldSlot.order];
                if (ex.workout) {
                  ex.workout.setProperty("hasChanged", true);
                  migrationNeeded = true;
                }
              }
            });
          }
        }

        if (oldSlot.series) {
          oldSlot.series.forEach((serieData) => {
            newExercise.fetchSerie(serieData);
          });
        }

        if (oldSlot.attributes) {
          oldSlot.attributes.forEach((attributeData) => {
            newExercise.fetchAttribute(attributeData);
          });
        }

        if (oldSlot.second_protocol_exercise) {
          console.log(
            "second_protocol_exercise for oldSlot.name",
            oldSlot.name,
          );
          const secondExercise = new Exercise(
            oldSlot.second_protocol_exercise.id,
            oldSlot.second_protocol_exercise.name,
            oldSlot.second_protocol_exercise.original_name,
            oldSlot.second_protocol_exercise.notes,
            oldSlot.second_protocol_exercise.order,
            undefined,
            false,
            null,
            [],
            [],
            undefined,
            this,
          );

          secondExercise.protocolSlot = newExercise.protocolSlot;
          secondExercise.protocolWeek = newExercise.protocolWeek;
          secondExercise.isSecondaryProtocol = true;

          if (secondaryProtocolsArray) {
            // console.log("if secondaryProtocolsArray", oldSlot.name);
            secondExercise.protocolText =
              secondaryProtocolsArray[oldSlot.order];
          }

          if (oldSlot.second_protocol_exercise.series) {
            // console.log("if series", oldSlot.name);
            oldSlot.second_protocol_exercise.series.forEach((serieData) => {
              secondExercise.fetchSerie(serieData);
            });
          }

          if (oldSlot.second_protocol_exercise.attributes) {
            // console.log("if attributes", oldSlot.name);
            oldSlot.second_protocol_exercise.attributes.forEach(
              (attributeData) => {
                secondExercise.fetchAttribute(attributeData);
              },
            );
          }
          // console.log("push protocols", secondExercise);
          this.protocols.push(secondExercise);
        }

        this.protocols.push(newExercise);
      });
      if (migrationNeeded) {
        if (this.client) {
          this.client.updateClientTransactionData(this.id.toLowerCase());
        } else if (this.coach) {
          this.coach.updateCoachTransactionBlocks(this.id.toLowerCase());
        }
      }
    }

    //Identify existing secondary protocol definitions
    const secondSlots = this.protocols.filter((p) => p.isSecondaryProtocol);
    // console.log(
    //   `[${blockName}] Identified ${secondSlots.length} secondary slots in this block.`
    // );
    if (secondSlots.length === 0) {
      // console.log(`[${blockName}] There are no secondarySlots in this block`);
      return;
    }

    // Derive basicExercise from slotType
    let basicExercise;
    if (slotType === "squat_slots") basicExercise = "squat";
    else if (slotType === "deadlift_slots") basicExercise = "deadlift";
    else if (slotType === "bench_press_slots") basicExercise = "benchPress";
    if (!basicExercise) {
      console.error(
        `[${blockName}] Skipping secondExercises migration due to unknown slotType`,
      );
      return;
    }
    // console.log(
    //   `[${blockName}] Derived basicExercise "${basicExercise}" from slotType "${slotType}".`
    // );

    // All workout exercises of that basic exercise
    const slotExercises = this.getAllExercisesArray.filter(
      (ex) => ex.getProtocolBasicExercise === basicExercise,
    );
    // console.log(
    //   `[${blockName}] Collected ${slotExercises.length} exercises for basicExercise "${basicExercise}".`
    // );

    //Here if slotExercises.length === 0, then return early
    if (slotExercises.length === 0) {
      // console.log(`[${blockName}] Returning early due to 0 slotExercises.`);
      return;
    }

    // Group exercises by protocolSlot
    const groupedBySlot = slotExercises.reduce((acc, ex) => {
      if (!ex.protocolSlot) return acc;
      if (!Object.prototype.hasOwnProperty.call(acc, ex.protocolSlot)) {
        acc[ex.protocolSlot] = [];
      }
      acc[ex.protocolSlot].push(ex);
      return acc;
    }, Object.create(null));

    // Helper: stable English name
    const getEnglishName = (obj) => {
      try {
        if (typeof obj.getName === "function") {
          return obj.getName((k) => k, "en");
        }
      } catch (_) {}
      return obj.name || obj.originalName || "";
    };

    for (let weekNumber = 1; weekNumber <= this.getWeeks; weekNumber++) {
      // console.log(
      //   `[${blockName}] Starting secondary protocol migration for week ${weekNumber}.`
      // );
      Object.values(groupedBySlot).forEach((exList) => {
        const weekSlotExercises = exList.filter(
          (ex) => ex.workout.week === weekNumber,
        );

        if (weekSlotExercises.length <= 1) return;

        const slotKey = exList[0].protocolSlot;
        // console.log(
        //   `[${blockName}] Processing slot "${slotKey}" for week ${weekNumber}.`
        // );

        // All protocol definitions for this slot
        const protocolDefs = this.protocols.filter(
          (p) => p.protocolSlot === slotKey,
        );
        if (protocolDefs.length === 0) return;

        const hasSecondaryAlready = weekSlotExercises.some(
          (e) => e.isSecondaryProtocol,
        );
        if (hasSecondaryAlready) return; // nothing to do for this week/slot

        const secondaryDefs = protocolDefs.filter((p) => p.isSecondaryProtocol);
        const primaryDefs = protocolDefs.filter((p) => !p.isSecondaryProtocol);

        const weekNamePairs = weekSlotExercises.map((ex) => ({
          ex,
          name: getEnglishName(ex),
        }));

        let assigned = false;

        // Strategy 1: unique name match with a secondary definition
        for (const sec of secondaryDefs) {
          const secName = getEnglishName(sec);
          const matches = weekNamePairs.filter(
            (w) => w.name === secName && !w.ex.isSecondaryProtocol,
          );
          if (matches.length === 1) {
            const target = matches[0].ex;
            target.setProperty("isSecondaryProtocol", true);

            // console.log(
            //   `[${blockName}] Strategy 1: Marked exercise "${target.name}" as secondary protocol.`
            // );
            if (sec.protocolText && weekNumber === 1) {
              target.setProperty("protocolText", sec.protocolText);
            }
            target.workout?.setProperty("hasChanged", true);
            assigned = true;
            break;
          }
          if (matches.length > 1) {
            console.log(
              `[${blockName}] Skipped Strategy 1 due to ambiguous case.`,
            );
            // ambiguous -> abort this strategy
            break;
          }
        }

        // Strategy 2: if not assigned, use primary unique match then mark another as secondary
        if (!assigned && primaryDefs.length > 0) {
          for (const prim of primaryDefs) {
            const primName = getEnglishName(prim);
            const matches = weekNamePairs.filter((w) => w.name === primName);
            if (matches.length === 1) {
              // Choose a different exercise (first not the primary match) as secondary
              const secondaryCandidate = weekSlotExercises.find(
                (e) => e !== matches[0].ex,
              );
              if (secondaryCandidate) {
                secondaryCandidate.setProperty("isSecondaryProtocol", true);
                // console.log(
                //   `[${blockName}] Strategy 2: Marked exercise "${secondaryCandidate.name}" as secondary protocol.`
                // );
                const textSource = secondaryDefs.find((p) => p.protocolText);
                if (textSource?.protocolText && weekNumber === 1) {
                  secondaryCandidate.setProperty(
                    "protocolText",
                    textSource.protocolText,
                  );
                }
                secondaryCandidate.workout?.setProperty("hasChanged", true);
                assigned = true;
              }
              break;
            }
            if (matches.length > 1) {
              console.log(
                `[${blockName}] Skipped Strategy 2 due to ambiguous case.`,
              );
              // ambiguous -> stop strategy 2
              break;
            }
          }
        }
      });
    }
    if (this.client) {
      this.client.updateClientTransactionData(this.id.toLowerCase());
    } else if (this.coach) {
      this.coach.updateCoachTransactionBlocks(this.id.toLowerCase());
    }
  }
  // #endregion

  // #region /* STATS & SUMMARY */
  get getExerciseUnitString() {
    let bool = false;
    if (this.client) {
      if (this.client.coach) {
        bool = this.client.coach.exerciseUnit;
      }
    } else if (this.coach) {
      bool = this.coach.exerciseUnit;
    }
    return bool ? "lbs" : "kg";
  }

  get getRirOrRpeString() {
    let bool = false;
    if (this.client) {
      if (this.client.coach) {
        bool = this.client.coach.rirOrRpe;
      }
    } else if (this.coach) {
      bool = this.coach.rirOrRpe;
    }
    return bool ? "RIR " : "@";
  }

  findBestSeriesInWeek(workouts, slot) {
    const bestExercise = workouts
      .flatMap((workout) => workout.exercises)
      .filter(
        (exercise) =>
          exercise.protocolSlot &&
          exercise.getProtocolBasicExercise === slot &&
          exercise.getProtocolPriority === "primary",
      )
      .reduce((best, exercise) => {
        const maxErm = exercise.getMaxErm; // Assuming getMaxErm is a method that returns the max ERM of the exercise
        return maxErm > (best?.getMaxErm || 0) ? exercise : best;
      }, null);

    const bestSerie = bestExercise?.series.reduce((best, serie) => {
      const maxErm = serie.getMaxErm; // Assuming getMaxErm is a method that returns the max ERM of the serie
      return maxErm > (best?.getMaxErm || 0) ? serie : best;
    }, null);

    return bestSerie;
  }

  blockPrimaryStats(basicExercise, stat, week) {
    // const allExercises = this.workouts.flatMap((workout) => workout.exercises);
    const relevantWorkouts =
      week !== undefined
        ? this.workouts.filter((workout) => workout.week === week)
        : this.workouts;

    // Flatten the exercises from the filtered workouts
    const allExercises = relevantWorkouts.flatMap(
      (workout) => workout.exercises,
    );
    const filteredExercises = allExercises.filter(
      (exercise) =>
        exercise.protocolSlot &&
        exercise.getProtocolBasicExercise === basicExercise &&
        exercise.getProtocolPriority === "primary",
    );

    function formatNumber(num) {
      if (Math.floor(num) === num) return num.toString();
      return parseFloat(num.toFixed(2)).toString();
    }
    function formatNumber1(num) {
      if (Math.floor(num) === num) return num.toString();
      return parseFloat(num.toFixed(1)).toString();
    }

    // If looking for the best ERM among exercises
    if (stat === "MaxErm") {
      const bestExerciseForMaxErm = filteredExercises.reduce(
        (best, current) => {
          const currentMaxErm = current.getMaxErm; // Assuming getMaxErm is a method
          return currentMaxErm > (best?.getMaxErm || 0) ? current : best;
        },
        null,
      );

      return bestExerciseForMaxErm
        ? `${bestExerciseForMaxErm.getMaxErm
            .toFixed(2)
            .replace(/\.00$|(\.\d0)$/, "")} ${this.getExerciseUnitString}`
        : "-";
    } else if (stat === "BestSerie") {
      // The original logic for finding the best series
      const bestExercise = filteredExercises.reduce((best, exercise) => {
        const maxErm = exercise.getMaxErm; // Assuming getMaxErm is a method
        return maxErm > (best?.getMaxErm || 0) ? exercise : best;
      }, null);

      const bestSerie = bestExercise?.series.reduce((best, serie) => {
        const maxErm = serie.getMaxErm; // Assuming getMaxErm is a method
        return maxErm > (best?.getMaxErm || 0) ? serie : best;
      }, null);

      if (!bestSerie) {
        // return "No series found for the given exercise.";
        return "-";
      }

      const weightDisplay = +bestSerie.weight.toFixed(2);
      const repsDisplay = bestSerie.reps;
      const rpeDisplay = +bestSerie.rpe.toFixed(1);

      return `${weightDisplay} ${this.getExerciseUnitString} x ${repsDisplay} ${this.getRirOrRpeString}${rpeDisplay}`;
    } else if (stat === "WeightIncrease") {
      // Initialize the variable to store the result
      let result;

      if (week !== undefined) {
        if (week === 1) {
          result = "-";
        } else {
          // Calculate the average weight difference between the specified week and the week before it
          const previousWeekWorkouts = this.workouts.filter(
            (workout) => workout.week === week - 1,
          );
          const targetWeekWorkouts = this.workouts.filter(
            (workout) => workout.week === week,
          );

          const previousWeekBestSerie = this.findBestSeriesInWeek(
            previousWeekWorkouts,
            basicExercise,
          );
          const targetWeekBestSerie = this.findBestSeriesInWeek(
            targetWeekWorkouts,
            basicExercise,
          );

          if (previousWeekBestSerie && targetWeekBestSerie) {
            // Calculate the weight difference between the best series of the previous week and the target week
            const weightDifference =
              targetWeekBestSerie.weight - previousWeekBestSerie.weight;
            const percentageIncrease =
              (weightDifference / previousWeekBestSerie.weight) * 100;
            // result = weightDifference.toFixed(2).replace(/\.00$|(\.\d0)$/, "");
            result =
              weightDifference === 0
                ? `0 ${this.getExerciseUnitString}`
                : `${formatNumber(weightDifference)} ${
                    this.getExerciseUnitString
                  } (${formatNumber1(percentageIncrease)}%)`;
          } else {
            // Handle case where either week does not have a best series
            result = "-";
          }
        }
      } else {
        // Week is undefined, calculate difference between first week and last week of the block
        const firstWeekWorkouts = this.workouts.filter(
          (workout) => workout.week === 1,
        );
        const lastWeekWorkouts = this.workouts.filter(
          (workout) => workout.week === this.getWeeks,
        );

        const firstWeekBestSerie = this.findBestSeriesInWeek(
          firstWeekWorkouts,
          basicExercise,
        );
        const lastWeekBestSerie = this.findBestSeriesInWeek(
          lastWeekWorkouts,
          basicExercise,
        );

        if (firstWeekBestSerie && lastWeekBestSerie) {
          // Calculate the weight difference between the best series of the first week and the last week
          const weightDifference =
            lastWeekBestSerie.weight - firstWeekBestSerie.weight;
          const percentageIncrease =
            (weightDifference / firstWeekBestSerie.weight) * 100;

          // result = weightDifference.toFixed(2).replace(/\.00$|0$/, "");
          result =
            weightDifference === 0
              ? "0 ${this.getExerciseUnitString}"
              : `${formatNumber(weightDifference)} ${
                  this.getExerciseUnitString
                } (${formatNumber1(percentageIncrease)}%)`;
        } else {
          // Handle case where either the first or the last week does not have a best series
          result = "-";
        }
      }

      return result === "-" ? result : result;
    } else {
      // Handle other stats if necessary
      return "-";
    }
  }

  blockDashboardTotalStressIndex(basicExercise, week) {
    const relevantWorkouts = this.workouts.filter(
      (workout) => week === undefined || workout.week === week,
    );

    const allExercises = relevantWorkouts.flatMap(
      (workout) => workout.exercises,
    );

    const filteredExercises = allExercises.filter(
      (exercise) =>
        exercise.protocolSlot &&
        exercise.getProtocolBasicExercise === basicExercise,
    );

    if (filteredExercises.length === 0) {
      return "-"; // No exercises found for the given exercise type.
    }

    const totalStressIndex = filteredExercises.reduce((sum, exercise) => {
      const stressIndex = exercise.getStressIndex || 0;
      return sum + stressIndex;
    }, 0);

    let result;
    if (week !== undefined) {
      result = totalStressIndex;
    } else {
      // If week is undefined, calculate the average stress index across all weeks
      const distinctWeeks = new Set(
        this.workouts.map((workout) => workout.week),
      ).size;
      result = totalStressIndex / distinctWeeks;
    }

    // Format the result to remove unnecessary decimals
    const formattedResult =
      result % 1 === 0 ? result.toString() : parseFloat(result.toFixed(1));

    return `${formattedResult}`;
  }

  blockDashboardAverageTonnage(basicExercise, week) {
    const relevantWorkouts =
      week !== undefined
        ? this.workouts.filter((workout) => workout.week === week)
        : this.workouts;

    const allExercises = relevantWorkouts.flatMap(
      (workout) => workout.exercises,
    );

    const filteredExercises = allExercises.filter(
      (exercise) =>
        exercise.protocolSlot &&
        exercise.getProtocolBasicExercise === basicExercise,
    );

    if (filteredExercises.length === 0) {
      return "-"; // No exercises found for the given exercise type.
    }

    const totalTonnage = filteredExercises.reduce((sum, exercise) => {
      const tonnage =
        typeof exercise.getTonnage === "function"
          ? exercise.getTonnage()
          : exercise.getTonnage || 0;
      return sum + tonnage;
    }, 0);

    let result;
    if (week !== undefined) {
      result = totalTonnage;
    } else {
      const distinctWeeks = new Set(
        this.workouts.map((workout) => workout.week),
      ).size;
      result = distinctWeeks > 0 ? totalTonnage / distinctWeeks : 0;
    }

    // Formatting the result based on whether it's an integer or a float
    // This checks if the result is an integer
    const isInteger = result % 1 === 0;

    // If it is an integer, convert it to string directly. If not, use toFixed(2) and then parseFloat to remove trailing zeros
    const formattedResult = isInteger
      ? result.toString()
      : parseFloat(result.toFixed(2));

    return `${formattedResult} ${this.getExerciseUnitString}`;
  }

  blockDashboardAverageSeries(basicExercise, week) {
    const relevantWorkouts =
      week !== undefined
        ? this.workouts.filter((workout) => workout.week === week)
        : this.workouts;

    const allExercises = relevantWorkouts.flatMap(
      (workout) => workout.exercises,
    );

    const filteredExercises = allExercises.filter(
      (exercise) =>
        exercise.protocolSlot &&
        exercise.getProtocolBasicExercise === basicExercise,
    );

    if (filteredExercises.length === 0) {
      return "-"; // No exercises found for the given exercise type.
    }

    const totalSeries = filteredExercises.reduce(
      (sum, exercise) => sum + exercise.series.length,
      0,
    );

    let result;
    if (week !== undefined) {
      result = totalSeries;
    } else {
      // Calculate the average series per week across the entire block
      const distinctWeeks = new Set(
        this.workouts.map((workout) => workout.week),
      ).size;
      const averageSeriesPerWeek =
        distinctWeeks > 0 ? totalSeries / distinctWeeks : 0;
      result = averageSeriesPerWeek;
    }

    // Format the result to remove unnecessary decimals, displaying with up to one decimal place only when needed
    const formattedResult =
      result % 1 === 0 ? result.toString() : parseFloat(result.toFixed(1));

    return `${formattedResult}`;
  }

  blockDashboardAverageNL(basicExercise, week) {
    // Filter relevant workouts by week if provided
    const relevantWorkouts =
      week !== undefined
        ? this.workouts.filter((workout) => workout.week === week)
        : this.workouts;

    // Get all exercises in those workouts
    const allExercises = relevantWorkouts.flatMap(
      (workout) => workout.exercises,
    );

    const filteredExercises = allExercises.filter(
      (exercise) =>
        exercise.protocolSlot &&
        exercise.getProtocolBasicExercise === basicExercise,
    );

    // If no exercises match, return a default value
    if (filteredExercises.length === 0) {
      return "-";
    }

    // Calculate total number of lifts across the filtered exercises
    const totalNL = filteredExercises.reduce(
      (sum, exercise) => sum + exercise.getNumberLifts,
      0,
    );

    // Determine result: either total for a specific week or average across all weeks
    let result;
    if (week !== undefined) {
      result = totalNL;
    } else {
      const distinctWeeks = new Set(
        this.workouts.map((workout) => workout.week),
      ).size;
      result = distinctWeeks > 0 ? totalNL / distinctWeeks : 0;
    }

    // Format the result to remove unnecessary decimals, displaying up to one decimal place if necessary
    const formattedResult =
      result % 1 === 0 ? result.toString() : parseFloat(result.toFixed(1));

    return `${formattedResult}`;
  }

  get minDate() {
    if (this.workouts.length > 0) {
      const validDates = this.workouts
        .filter((workout) => workout.date)
        .map((workout) => new Date(workout.date).getTime());
      if (validDates.length === 0) return null;
      const minTimestamp = Math.min(...validDates);
      return new Date(minTimestamp).getTime();
    }
    return new Date();
  }

  get maxDate() {
    if (this.workouts.length > 0) {
      // Filter out workouts without a date and find the workout with the latest date
      const latestWorkout = this.workouts
        .filter((workout) => workout.date)
        .reduce(
          (latest, workout) => {
            const workoutDate = new Date(workout.date).getTime();
            return workoutDate > latest.date
              ? { date: workoutDate, week: workout.week }
              : latest;
          },
          { date: 0, week: 0 },
        );

      if (latestWorkout.date === 0) {
        return null; // No valid latest workout found
      }

      // Calculate the estimated end date
      const totalWeeks = this.totalWeeks;
      const remainingWeeks = totalWeeks - latestWorkout.week;
      const estimatedEndDate = new Date(latestWorkout.date);
      estimatedEndDate.setDate(estimatedEndDate.getDate() + remainingWeeks * 7);

      return estimatedEndDate.getTime(); // Return as timestamp
    }
    return null; // or a default date if needed
  }

  getAllExercisesNames(t, language) {
    //Per metriques
    const allExercises = this.workouts
      .flat() // Flatten block workouts before processing
      .map((workout) => workout.exercises) // Get exercises from each workout
      .flat(); // Flatten exercises into a single array

    // Use Set to automatically remove duplicates based on object reference
    const uniqueExercises = new Set(
      allExercises.map((exercise) => exercise.getName(t, language)),
    );

    // Convert Set back to an array of names
    return Array.from(uniqueExercises);
  }

  get getAllExercisesArray() {
    const allExercises = this.workouts
      .flat() // Flatten block workouts before processing
      .map((workout) => workout.exercises) // Get exercises from each workout
      .flat(); // Flatten exercises into a single array

    // Convert Set back to an array of names
    return allExercises;
  }
  // #endregion

  // #region /* CALENDAR */
  get initialDate() {
    const workoutsWithDate = this.workouts.filter((w) => w.date !== null);

    if (workoutsWithDate.length > 0) {
      const sortedWorkouts = workoutsWithDate.sort((a, b) => a.date - b.date);
      return sortedWorkouts[0].date;
    } else if (this.calendarStartDate) {
      return this.calendarStartDate;
    } else {
      return null;
    }
  }

  endDate(totalWeeks = this.totalWeeks) {
    const start = this.initialDate;
    if (!start) return null;
    const d = new Date(start);
    d.setDate(d.getDate() + (totalWeeks - 1) * 7);
    const sunday = getSundayOfWeek(d, true);
    return sunday;
  }

  get effectiveTotalWeeks() {
    // Base weeks from the block itself
    const baseWeeks = Math.max(1, Number(this.totalWeeks) || 0);

    // Safety: no start date? Can't expand without an origin.
    if (!this.initialDate) return baseWeeks;

    const weekMs = 7 * 24 * 60 * 60 * 1000;

    // Resolve workouts that have a valid Date
    const datedWorkouts = (this.workouts || [])
      .map((w) => {
        const raw = w?.date;
        const d =
          typeof raw?.toDate === "function"
            ? raw.toDate()
            : raw
              ? new Date(raw)
              : null;
        return d instanceof Date && !isNaN(d) ? { w, d } : null;
      })
      .filter(Boolean);

    // If there are no workouts with dates, rely directly on totalWeeks
    if (datedWorkouts.length === 0) return baseWeeks;

    const startMon = getMondayOfWeek(this.initialDate, true);

    // Approach A: use the absolute latest workout date among all
    const lastAny = new Date(
      Math.max(...datedWorkouts.map((x) => x.d.getTime())),
    );
    const lastAnyMon = getMondayOfWeek(lastAny, true);
    const weeksByAny = Math.round((lastAnyMon - startMon) / weekMs) + 1;

    // Approach B: pick workouts from the highest .week, take the highest .date,
    // then add the missing weeks (totalWeeks - foundWorkout.week)
    const maxWeekWithDate = Math.max(
      ...datedWorkouts.map((x) => x.w.week || 0),
    );
    const inMaxWeek = datedWorkouts.filter(
      (x) => (x.w.week || 0) === maxWeekWithDate,
    );

    let weeksByEst = 0;
    if (inMaxWeek.length > 0) {
      const lastInMaxWeek = new Date(
        Math.max(...inMaxWeek.map((x) => x.d.getTime())),
      );
      const missingWeeks = Math.max(0, baseWeeks - (maxWeekWithDate || 0));
      const estEndDate = new Date(
        lastInMaxWeek.getTime() + missingWeeks * weekMs,
      );
      const estEndMon = getMondayOfWeek(estEndDate, true);
      weeksByEst = Math.round((estEndMon - startMon) / weekMs) + 1;
    }

    // Take the maximum between: approach A and approach B
    return Math.max(weeksByAny, weeksByEst || 0);
  }

  get getBlockDashboardWidth() {
    const INITIAL_SPACE = 196;
    const END_SPACE = 34;
    const WEEK_GAP = 40;
    // const EXTERNAL_GAP = 16;
    const weekWidth = this?.showingDashboardActual ? 264 + 216 : 264;
    const numWeeks = this?.totalWeeks || 0;
    const gaps = numWeeks > 1 ? (numWeeks - 1) * WEEK_GAP : 0;
    return INITIAL_SPACE + END_SPACE + numWeeks * weekWidth + gaps;
    // + EXTERNAL_GAP
  }

  // #endregion

  // #region /* BLOCK LIBRARY */
  uploadToCloudLibrary() {
    let workoutsArray = this.workouts.map((workout) => workout.uploadToCloud());

    let protocols = this.protocols.map((exercise) => {
      return exercise.uploadToCloud();
    });

    const data = {
      id: this.id.toString().toLowerCase(),
      name: this.name,
      notes: this.notes,
      weeks: 1,
      total_weeks: this.totalWeeks,
      label: "",
      workouts: workoutsArray,
      ...(Number.isFinite(Number(this.order))
        ? { order: Number(this.order) }
        : {}),
      ...(this.protocols.length > 0 ? { protocols: protocols } : {}),
      ...(this.hiddenWeeks.length > 0
        ? { hidden_weeks: this.hiddenWeeks }
        : {}),
      ...(Object.keys(this.weekNames || {}).length > 0
        ? { week_names: this.weekNames }
        : {}),
    };

    return data;
  }
  copyBlockToLibrary(showToast) {
    if (this.client.coach) {
      const nextOrder = Array.isArray(this.client.coach.blockLibrary)
        ? this.client.coach.blockLibrary.reduce((maxOrder, entry) => {
            const parsed = Number(entry?.order);
            if (!Number.isFinite(parsed)) return maxOrder;
            return parsed > maxOrder ? parsed : maxOrder;
          }, -1) + 1
        : 0;

      const newBlock = new Block(
        undefined,
        this.name,
        this.notes,
        [],
        false,
        false,
        null,
        1,
        this.totalWeeks,
        undefined,
        null,
        this.client.coach,
        nextOrder,
      );

      newBlock.setProperty("weekNames", this.weekNames);
      newBlock.setProperty("hiddenWeeks", this.hiddenWeeks);

      this.protocols.forEach((slotExercise) => {
        const newExercise = new Exercise(
          undefined,
          slotExercise.name,
          slotExercise.originalName,
          slotExercise.notes,
          slotExercise.order,
          undefined,
          false,
          null,
          [],
          [],
          undefined,
          newBlock,
          slotExercise.link,
        );

        newExercise.isSecondaryProtocol = slotExercise.isSecondaryProtocol;
        newExercise.protocolSlot = slotExercise.protocolSlot;
        newExercise.protocolText = slotExercise.protocolText;
        newExercise.protocolWeek = slotExercise.protocolWeek;

        slotExercise.series?.forEach((serie) => {
          newExercise.copySerie(serie);
        });
        slotExercise.attributes?.forEach((attribute) => {
          newExercise.copyAttribute(attribute);
        });

        newBlock.protocols.push(newExercise);
      });

      this.workouts
        .sort((a, b) => a.week - b.week || a.order - b.order)
        .forEach((workout) => {
          const newWorkout = new Workout(
            undefined,
            null,
            workout.name,
            workout.order,
            workout.week,
            false,
            newBlock,
            [],
          );

          let usedOrderValues = new Set();
          const sortedExercises =
            workout.exercises &&
            [...workout.exercises].sort((a, b) => a.order - b.order);

          sortedExercises?.forEach((exercise, index) => {
            let exerciseOrder = exercise.order;

            // Ensure that no two exercises share the same order
            while (usedOrderValues.has(exerciseOrder)) {
              exerciseOrder++; // Increment the order if it's already used
            }

            // Track the exercise order
            usedOrderValues.add(exerciseOrder);

            const isSelected = index === 0 ? true : false;

            const newExercise = new Exercise(
              undefined,
              exercise.name,
              exercise.originalName,
              exercise.notes,
              exerciseOrder,
              true,
              isSelected,
              newWorkout,
              [],
              [],
              undefined,
              undefined,
              exercise.link,
            );

            newExercise.protocolSlot = exercise.protocolSlot;
            newExercise.protocolText = exercise.protocolText;
            newExercise.isSecondaryProtocol = exercise.isSecondaryProtocol;

            exercise.series?.forEach((serie) => {
              newExercise.copySerie(serie);
            });

            exercise.attributes?.forEach((attribute) => {
              newExercise.copyAttribute(attribute);
            });

            newWorkout.exercises.push(newExercise);
          });

          newBlock.workouts.push(newWorkout);
        });

      this.client.coach.blockLibrary.push(newBlock);
      this.client.coach.updateCoachTransactionBlocks(newBlock.id);
      this.client.coach.startOrResetBatchTimer();

      showToast("blockExportedToLibrary-string", {
        type: "info",
        description: "blockExportedToLibraryDescription-string",
        variables: {
          blockName: this.name,
        },
      });
    }
  }
  // #endregion

  // #region /* WORKOUT ACTIONS */
  deleteWorkout(workout) {
    // Find the index of the workout to be deleted
    const index = this.workouts.findIndex((w) => w === workout);

    // Check if the workout was found
    if (index > -1) {
      const workoutId = workout.id.toLowerCase();

      //Apply changes to slotExercises
      workout.exercises.forEach((e) => e.copyExerciseToBlockProtocols());

      // Add the workout ID to the clientDeletionWorkouts array if not already present
      if (
        this.client &&
        !this.client.clientDeletionWorkouts.includes(workoutId)
      ) {
        this.client.clientDeletionWorkouts.push(workoutId);
      }

      // Handle week-related logic
      if (workout.week > 0) {
        const weekWorkouts = this.workouts.filter(
          (w) => w.week === workout.week,
        );

        if (weekWorkouts.length === 1) {
          if (workout.week === this.getWeeks) {
            if (this.getWeeks > 1) {
              this.selectedWeek--;
            }
          } else {
            for (let i = workout.week + 1; i <= this.getWeeks; i++) {
              this.workouts
                .filter((w) => w.week === i)
                .forEach((w) => w.setProperty("week", w.week - 1));
            }
            if (this.getWeeks > 1) {
              if (
                this.selectedWeek > this.getWeeks ||
                this.selectedWeek === 0
              ) {
                this.selectedWeek = this.getWeeks;
              }
            }
          }
        }
      }

      // Remove the workout from the array
      this.workouts.splice(index, 1);

      // Update client transaction data
      if (this.client) {
        this.client.updateClientTransactionData(workout.block.id.toLowerCase());
        this.client.startOrResetBatchTimer();
      } else {
        if (this.coach) {
          this.coach.updateCoachTransactionBlocks(this.id.toLowerCase());
          this.coach.startOrResetBatchTimer();
        }
      }
    }
  }

  deleteWorkoutWithoutReordering(workout) {
    // Find the index of the workout to be deleted
    const index = this.workouts.findIndex((w) => w === workout);

    // Check if the workout was found
    if (index > -1) {
      const workoutId = workout.id.toLowerCase();

      //Apply changes to slotExercises
      workout.exercises.forEach((e) => e.copyExerciseToBlockProtocols());

      // Add the workout ID to the clientDeletionWorkouts array if not already present
      if (
        this.client &&
        !this.client.clientDeletionWorkouts.includes(workoutId)
      ) {
        this.client.clientDeletionWorkouts.push(workoutId);
      }

      // Remove the workout from the array (no week reordering)
      this.workouts.splice(index, 1);

      // Update client transaction data
      if (this.client) {
        this.client.updateClientTransactionData(workout.block.id.toLowerCase());
        this.client.startOrResetBatchTimer();
      } else {
        if (this.coach) {
          this.coach.updateCoachTransactionBlocks(this.id.toLowerCase());
          this.coach.startOrResetBatchTimer();
        }
      }
    }
  }

  deleteWeek(week) {
    if (week) {
      // Filter workouts to be deleted
      const weekWorkouts = this.workouts.filter((weekW) => weekW.week === week);

      // Create a copy of workouts to avoid modifying the array while iterating
      const workoutsToDelete = [...weekWorkouts];

      // Delete each workout
      workoutsToDelete.forEach((workoutToDelete) => {
        const workoutId = workoutToDelete.id.toLowerCase();

        //Apply changes to slotExercises
        workoutToDelete.exercises.forEach((e) =>
          e.copyExerciseToBlockProtocols(),
        );

        if (!this.client.clientDeletionWorkouts.includes(workoutId)) {
          this.client.clientDeletionWorkouts.push(workoutId);
        }

        const index = this.workouts.findIndex(
          (w) => w.id === workoutToDelete.id,
        );
        if (index !== -1) {
          this.workouts.splice(index, 1);
        }
      });

      // Adjust the weeks of remaining workouts
      for (let i = week + 1; i <= this.getWeeks; i++) {
        this.workouts
          .filter((w) => w.week === i)
          .forEach((w) => w.setProperty("week", w.week - 1));
      }

      // Adjust the total number of weeks
      if (this.getWeeks >= 1) {
        if (this.selectedWeek > this.getWeeks || this.selectedWeek === 0) {
          this.selectedWeek = this.getWeeks;
        }
      }

      // Update client transaction data
      this.client.updateClientTransactionData(this.id.toLowerCase());

      // Start or reset the batch timer
      this.client.startOrResetBatchTimer();
    }
  }

  deleteWeekWorkoutsWithoutReordering(week) {
    if (week) {
      // Filter workouts to be deleted
      const weekWorkouts = this.workouts.filter((weekW) => weekW.week === week);

      // Create a copy of workouts to avoid modifying the array while iterating
      const workoutsToDelete = [...weekWorkouts];

      // Delete each workout
      workoutsToDelete.forEach((workoutToDelete) => {
        const workoutId = workoutToDelete.id.toLowerCase();

        //Apply changes to slotExercises
        workoutToDelete.exercises.forEach((e) =>
          e.copyExerciseToBlockProtocols(),
        );

        if (
          this.client &&
          !this.client.clientDeletionWorkouts.includes(workoutId)
        ) {
          this.client.clientDeletionWorkouts.push(workoutId);
        }

        const index = this.workouts.findIndex(
          (w) => w.id === workoutToDelete.id,
        );
        if (index !== -1) {
          this.workouts.splice(index, 1);
        }
      });

      // Update client transaction data (no week reordering)
      if (this.client) {
        this.client.updateClientTransactionData(this.id.toLowerCase());
        this.client.startOrResetBatchTimer();
      } else {
        if (this.coach) {
          this.coach.updateCoachTransactionBlocks(this.id.toLowerCase());
          this.coach.startOrResetBatchTimer();
        }
      }
    }
  }

  moveWorkout(workout, direction) {
    const sameWeekWorkouts = this.selectedWeekWorkouts;
    // Find the index of the workout in the same week array
    const index = sameWeekWorkouts.findIndex((w) => w.id === workout.id);
    if (index === -1) return; // Exit if workout not found in the filtered array

    // Determine the new index based on the direction
    const newIndex = direction === "up" ? index - 1 : index + 1;

    // Check if the new index is within bounds
    if (newIndex >= 0 && newIndex < sameWeekWorkouts.length) {
      // Swap the workouts
      [sameWeekWorkouts[index], sameWeekWorkouts[newIndex]] = [
        sameWeekWorkouts[newIndex],
        sameWeekWorkouts[index],
      ];
    }

    // Update the order of workouts in the same week
    sameWeekWorkouts.forEach((w, idx) => {
      w.order = idx;
    });
  }
  // #endregion

  // #region /* BLOCK PROTOCOLS */
  canShowProtocolRow(exercise, previousProtocol) {
    const priorities = [
      "primary",
      "secondary",
      "tertiary",
      "quaternary",
      "quinary",
    ];
    const prevIdx = priorities.indexOf(previousProtocol);
    if (prevIdx === -1) return false;

    // Determine the maximum priority index that has any exercise for this slot
    const presentIndices = this.protocolsAndExercises
      .filter((ex) => ex.getProtocolBasicExercise === exercise)
      .map((ex) => priorities.indexOf(ex.getProtocolPriority))
      .filter((idx) => idx >= 0);

    const maxIdx = presentIndices.length > 0 ? Math.max(...presentIndices) : -1;

    // Show the row if its previousProtocol index is <= maxIdx (one past the last populated slot)
    return prevIdx <= maxIdx;
  }

  slotExercisesForLayout(slot, selectedWeek = this.splitSelectedWeek) {
    // Exclude any protocol slots already used in this week’s workouts
    const takenProtocolSlots = new Set(
      this.workouts
        .filter((w) => w.week === selectedWeek)
        .flatMap((w) => w.exercises)
        .map((e) => e.protocolSlot),
    );
    if (slot === "squat") {
      return this.protocols.filter(
        (slotExercise) =>
          (slotExercise.protocolSlot === ProtocolTitles.PRIMARY_SQUAT ||
            slotExercise.protocolSlot === ProtocolTitles.SECONDARY_SQUAT ||
            slotExercise.protocolSlot === ProtocolTitles.TERTIARY_SQUAT ||
            slotExercise.protocolSlot === ProtocolTitles.QUATERNARY_SQUAT ||
            slotExercise.protocolSlot === ProtocolTitles.QUINARY_SQUAT) &&
          slotExercise.protocolWeek === selectedWeek &&
          !slotExercise.isSecondaryProtocol &&
          !takenProtocolSlots.has(slotExercise.protocolSlot),
      );
    } else if (slot === "benchPress") {
      return this.protocols.filter(
        (slotExercise) =>
          (slotExercise.protocolSlot === ProtocolTitles.PRIMARY_BENCH_PRESS ||
            slotExercise.protocolSlot ===
              ProtocolTitles.SECONDARY_BENCH_PRESS ||
            slotExercise.protocolSlot === ProtocolTitles.TERTIARY_BENCH_PRESS ||
            slotExercise.protocolSlot ===
              ProtocolTitles.QUATERNARY_BENCH_PRESS ||
            slotExercise.protocolSlot === ProtocolTitles.QUINARY_BENCH_PRESS) &&
          slotExercise.protocolWeek === selectedWeek &&
          !slotExercise.isSecondaryProtocol &&
          !takenProtocolSlots.has(slotExercise.protocolSlot),
      );
    } else {
      return this.protocols.filter(
        (slotExercise) =>
          (slotExercise.protocolSlot === ProtocolTitles.PRIMARY_DEADLIFT ||
            slotExercise.protocolSlot === ProtocolTitles.SECONDARY_DEADLIFT ||
            slotExercise.protocolSlot === ProtocolTitles.TERTIARY_DEADLIFT ||
            slotExercise.protocolSlot === ProtocolTitles.QUATERNARY_DEADLIFT ||
            slotExercise.protocolSlot === ProtocolTitles.QUINARY_DEADLIFT) &&
          slotExercise.protocolWeek === selectedWeek &&
          !slotExercise.isSecondaryProtocol &&
          !takenProtocolSlots.has(slotExercise.protocolSlot),
      );
    }
  }

  getAllExercisesFromOtherWorkoutsOfWeek(workout) {
    const allExercises = this.workouts
      .filter((w) => w.id !== workout.id && w.week === workout.week)
      .map((workoutToArray) => workoutToArray.exercises) // Get exercises from each workout
      .flat(); // Flatten exercises into a single array

    return allExercises;
  }

  slotsForLayout(basicExercise) {
    const options =
      basicExercise === "squat-string"
        ? [
            ProtocolTitles.PRIMARY_SQUAT,
            ProtocolTitles.SECONDARY_SQUAT,
            ProtocolTitles.TERTIARY_SQUAT,
            ProtocolTitles.QUATERNARY_SQUAT,
            ProtocolTitles.QUINARY_SQUAT,
          ]
        : basicExercise === "benchPress-string"
          ? [
              ProtocolTitles.PRIMARY_BENCH_PRESS,
              ProtocolTitles.SECONDARY_BENCH_PRESS,
              ProtocolTitles.TERTIARY_BENCH_PRESS,
              ProtocolTitles.QUATERNARY_BENCH_PRESS,
              ProtocolTitles.QUINARY_BENCH_PRESS,
            ]
          : [
              ProtocolTitles.PRIMARY_DEADLIFT,
              ProtocolTitles.SECONDARY_DEADLIFT,
              ProtocolTitles.TERTIARY_DEADLIFT,
              ProtocolTitles.QUATERNARY_DEADLIFT,
              ProtocolTitles.QUINARY_DEADLIFT,
            ];

    return options
      .map((protocolSlot) =>
        this.protocols.find(
          (slot) =>
            slot.protocolSlot === protocolSlot &&
            slot.protocolWeek === this.selectedWeek,
        ),
      )
      .filter(Boolean);
  }

  get protocolsAndExercises() {
    // collect every workout exercise with a protocolSlot
    const blockExercises = this.workouts.flatMap((workout) =>
      workout.exercises.filter((ex) => !!ex.protocolSlot),
    );
    // merge protocol definitions and real exercises
    return [...this.protocols, ...blockExercises];
  }

  // get firstExercisesFromProtocols(){
  //   this.protocols.filter(())
  // }

  getSlotOfWeek(slot, week) {
    const blockExercise = this.workouts
      .flatMap((w) => w.exercises)
      .find(
        (ex) =>
          ex.protocolSlot === slot &&
          ex.getWeek === week &&
          !ex.isSecondaryProtocol,
      );

    if (blockExercise) return blockExercise;

    const protocolExercise = this.protocols.find(
      (ex) =>
        ex.protocolSlot === slot &&
        ex.getWeek === week &&
        !ex.isSecondaryProtocol,
    );

    return protocolExercise || null;
  }

  /**
   * Get exercises (primary and optionally secondary) for a given slot and week
   * First checks workouts, then falls back to protocol definitions
   * @param {string} slot - Protocol slot identifier (e.g., "primarySquat-string")
   * @param {number} week - Week number
   * @returns {Object|null} Object with { primary: Exercise, secondary: Exercise|null } or null if not found
   */
  getExercisesForSlotAndWeek(slot, week) {
    if (!slot || !week) return null;

    // First, try to get exercises from workouts
    const workoutExercises = this.workouts
      .flatMap((w) => w.exercises)
      .filter((ex) => ex.protocolSlot === slot && ex.getWeek === week);

    if (workoutExercises.length > 0) {
      // Find primary exercise (not secondary protocol)
      const primaryExercise = workoutExercises.find(
        (ex) => !ex.isSecondaryProtocol,
      );

      if (primaryExercise) {
        // Find secondary exercise if it exists
        const secondaryExercise = workoutExercises.find(
          (ex) => ex.isSecondaryProtocol,
        );

        return {
          primary: primaryExercise,
          secondary: secondaryExercise || null,
        };
      }
    }

    // If no workout exercises found, check protocol definitions
    const protocolExercises = this.protocols.filter(
      (ex) => ex.protocolSlot === slot && ex.protocolWeek === week,
    );

    if (protocolExercises.length === 0) return null;

    // Find primary exercise (not secondary protocol)
    const primaryExercise = protocolExercises.find(
      (ex) => !ex.isSecondaryProtocol,
    );

    if (!primaryExercise) return null;

    // Find secondary exercise if it exists
    const secondaryExercise = protocolExercises.find(
      (ex) => ex.isSecondaryProtocol,
    );

    return {
      primary: primaryExercise,
      secondary: secondaryExercise || null,
    };
  }

  hasAnyExerciseForSlot(protocolTitle) {
    for (let week = 1; week <= this.totalWeeks; week++) {
      if (this.getSlotOfWeek(protocolTitle, week)) {
        return true;
      }
    }
    return false;
  }

  hasAnyExerciseForBasicExercise(basicExercise) {
    // 1) Pull out all real workout‐exercises and all protocol‐exercises
    const blockExercises = this.workouts.flatMap((w) => w.exercises);
    const protocolExercises = this.protocols;

    // 2) Combine them into one array
    const allEx = [...blockExercises, ...protocolExercises];

    // 3) Check if any matches your criteria
    return allEx.some((ex) => ex.getProtocolBasicExercise === basicExercise);
  }

  get selectedSlot() {
    const slot = this.protocolsAndExercises.find(
      (s) => s.id.toLowerCase() === this.selectedSlotId,
    );

    return slot ? slot : null;
  }

  addNewSlot(predefinedExercise, t, attribute) {
    //TEMA ORIGINAL NAME NO CAL SI FIQUEM EL POPUP DE ATTS I QUE NO ES PUGUI SELECCIONAR UN ATT SENSE HAVER CREAT EXERCICI PRIMER

    const originalName =
      this.selectedProtocolSlot === ProtocolTitles.PRIMARY_SQUAT ||
      this.selectedProtocolSlot === ProtocolTitles.SECONDARY_SQUAT ||
      this.selectedProtocolSlot === ProtocolTitles.TERTIARY_SQUAT ||
      this.selectedProtocolSlot === ProtocolTitles.QUATERNARY_SQUAT ||
      this.selectedProtocolSlot === ProtocolTitles.QUINARY_SQUAT
        ? "squat-string"
        : this.selectedProtocolSlot === ProtocolTitles.PRIMARY_BENCH_PRESS ||
            this.selectedProtocolSlot ===
              ProtocolTitles.SECONDARY_BENCH_PRESS ||
            this.selectedProtocolSlot === ProtocolTitles.TERTIARY_BENCH_PRESS ||
            this.selectedProtocolSlot ===
              ProtocolTitles.QUATERNARY_BENCH_PRESS ||
            this.selectedProtocolSlot === ProtocolTitles.QUINARY_BENCH_PRESS
          ? "benchPress-string"
          : "sumoDeadlift-string";

    const newExercise = new Exercise(
      uuidv4(),
      predefinedExercise ? t(predefinedExercise.name) : t(originalName),
      predefinedExercise ? predefinedExercise.originalName : originalName,
      "",
      0,
      true,
      false,
      null,
      [],
      [],
      null,
      this,
      predefinedExercise ? predefinedExercise.link : null,
    );

    newExercise.setProperty("protocolSlot", this.selectedProtocolSlot);
    newExercise.setProperty("protocolWeek", this.selectedProtocolWeek);

    for (let i = 0; i < 3; i++) {
      const newSerie = new Serie(
        uuidv4(),
        i,
        0,
        0,
        0,
        undefined,
        undefined,
        undefined,
        newExercise,
        3,
      );
      newExercise.series.push(newSerie);
    }

    this.protocols.push(newExercise);

    this.selectedSlotId = newExercise.id.toLowerCase();

    if (predefinedExercise === null) {
      if (this.client) {
        this.client.coach.selectAttribute(newExercise, attribute, t, false);
      } else if (this.coach) {
        this.coach.selectAttribute(newExercise, attribute, t, false);
      }
    }

    if (this.client) {
      if (this.client?.avoidTransaction === false) {
        this.client.updateClientTransactionData(this.id.toLowerCase());
        this.client.startOrResetBatchTimer();
      }
    } else {
      if (this.coach) {
        this.coach.updateCoachTransactionBlocks(this.id.toLowerCase());
        this.coach.startOrResetBatchTimer();
      }
    }
  }

  /**
   * Add a new slot from ExerciseSearchFloating result
   * @param {Object} searchResult - Result object from ExerciseSearchFloating
   *   {
   *     exerciseId: string,
   *     exerciseOriginalName: string,
   *     exerciseName: string,
   *     link: string,
   *     modifiers: Array<{id, originalName, name, type, subtype}> // name is localized
   *   }
   * @param {Function} t - Translation function
   * @param {string} selectedProtocolSlot - The protocol slot
   * @param {number} selectedProtocolWeek - The protocol week
   * @param {boolean} isSecondProtocol - Whether this is a second protocol exercise
   * @param {Object} primaryExercise - The primary exercise (required if isSecondProtocol is true)
   */
  addSlotFromSearchResult(
    searchResult,
    t,
    selectedProtocolSlot,
    selectedProtocolWeek,
    isSecondProtocol = false,
    primaryExercise = null,
  ) {
    if (!searchResult) return;

    // If this is a second protocol, create it on the primary exercise
    if (isSecondProtocol && primaryExercise) {
      const coach = this.client?.coach || this.coach;
      if (!coach) return;

      // Use the new method that directly creates the second protocol with search result data
      primaryExercise.addSecondProtocolFromSearchResult(searchResult, t, coach);

      // Update transactions
      if (this.client) {
        if (this.client?.avoidTransaction === false) {
          this.client.updateClientTransactionData(this.id.toLowerCase());
          this.client.startOrResetBatchTimer();
        }
      } else {
        if (this.coach) {
          this.coach.updateCoachTransactionBlocks(this.id.toLowerCase());
          this.coach.startOrResetBatchTimer();
        }
      }
      return;
    }

    // Create new exercise using similar logic to addNewSlot
    const newExercise = new Exercise(
      uuidv4(),
      searchResult.exerciseName, // Use the localized name
      searchResult.exerciseOriginalName,
      "",
      0,
      true,
      false,
      null,
      [],
      [],
      null,
      this,
      typeof searchResult?.link === "string" ? searchResult.link : "",
    );

    newExercise.setProperty("protocolSlot", selectedProtocolSlot);
    newExercise.setProperty("protocolWeek", selectedProtocolWeek);

    // Add default series
    for (let i = 0; i < 3; i++) {
      const newSerie = new Serie(
        uuidv4(),
        i,
        0,
        0,
        0,
        undefined,
        undefined,
        undefined,
        newExercise,
        3,
      );
      newExercise.series.push(newSerie);
    }

    // Add to protocols
    this.protocols.push(newExercise);
    this.selectedSlotId = newExercise.id.toLowerCase();

    // Apply modifiers (attributes) if any.
    // IMPORTANT: some modifiers may no longer exist in customPredefinedAttributes.
    // In that case, still materialize them on the exercise so the full name and
    // future editing flows remain consistent.
    if (
      Array.isArray(searchResult.modifiers) &&
      searchResult.modifiers.length > 0
    ) {
      const EXCLUSIVE_TITLES = new Set([
        Attribute.AttributeTitles.equipment,
        Attribute.AttributeTitles.tempo,
        Attribute.AttributeTitles.rom,
        Attribute.AttributeTitles.stanceGrip,
      ]);

      searchResult.modifiers.forEach((modifier) => {
        if (!modifier) return;

        const originalName = (modifier.originalName || "").trim();
        if (!originalName) return;

        const title = (modifier.type || "").trim();
        const subtitle = modifier.subtype ? String(modifier.subtype) : "";

        // Enforce exclusivity at the exercise level (defensive).
        if (title && EXCLUSIVE_TITLES.has(title)) {
          newExercise.attributes = (newExercise.attributes || []).filter(
            (a) => (a?.title || "") !== title,
          );
        }

        // Always materialize from payload so missing modifiers still apply.
        const attributeToAdd = new Attribute(
          undefined,
          originalName,
          title,
          subtitle,
          0,
          false,
          false,
          null,
          newExercise,
        );

        newExercise.attributes.push(attributeToAdd);
      });

      // Preserve the full desired name (including missing modifiers).
      if (searchResult.exerciseName) {
        newExercise.setProperty("name", searchResult.exerciseName);
      }
    }

    // Update transactions
    if (this.client) {
      if (this.client?.avoidTransaction === false) {
        this.client.updateClientTransactionData(this.id.toLowerCase());
        this.client.startOrResetBatchTimer();
      }
    } else {
      if (this.coach) {
        this.coach.updateCoachTransactionBlocks(this.id.toLowerCase());
        this.coach.startOrResetBatchTimer();
      }
    }
  }

  /**
   * Update an existing slot from ExerciseSearchFloating result
   * @param {Object} existingExercise - The exercise to update
   * @param {Object} searchResult - Result object from ExerciseSearchFloating
   * @param {Function} t - Translation function
   * @param {boolean} isSecondProtocol - Whether this is a second protocol exercise
   */
  updateSlotFromSearchResult(
    existingExercise,
    searchResult,
    t,
    isSecondProtocol = false,
  ) {
    if (!existingExercise || !searchResult) return;

    const coach = this.client?.coach || this.coach;
    if (!coach) return;

    // Determine which exercise to actually update
    // If isSecondProtocol is true but existingExercise is not a second protocol,
    // it means we need to update the second protocol of this exercise
    let targetExercise = existingExercise;
    if (isSecondProtocol && !existingExercise.isSecondaryProtocol) {
      // We're updating the second protocol, not the primary
      targetExercise = existingExercise.getSecondProtocol;
      if (!targetExercise) {
        console.warn("No second protocol found to update");
        return;
      }
    }

    // Update the base exercise if it changed
    if (targetExercise.originalName !== searchResult.exerciseOriginalName) {
      targetExercise.setProperty(
        "originalName",
        searchResult.exerciseOriginalName,
      );
    }

    const normalizeString = (value) => String(value ?? "").trim();
    const keyForAttribute = (attr) => {
      const name = normalizeString(attr?.name);
      const title = normalizeString(attr?.title);
      const subtitle = normalizeString(attr?.subtitle);
      return `${name}|${title}|${subtitle}`;
    };

    const rawModifiers = Array.isArray(searchResult.modifiers)
      ? searchResult.modifiers
      : [];

    // Keep name consistent with modifier presence (and update it even if modifiers didn't change)
    const desiredName =
      rawModifiers.length > 0
        ? searchResult.exerciseName
        : t(searchResult.exerciseOriginalName);
    if (targetExercise.name !== desiredName) {
      targetExercise.setProperty("name", desiredName);
    }

    const desiredLink =
      typeof searchResult?.link === "string" ? searchResult.link : "";
    if (targetExercise.link !== desiredLink) {
      targetExercise.setProperty("link", desiredLink);
    }

    const normalizeModifier = (modifier) => {
      if (!modifier) return null;
      const originalName = normalizeString(modifier.originalName);
      if (!originalName) return null;
      const type = normalizeString(modifier.type);
      const subtype = normalizeString(modifier.subtype);
      const id = normalizeString(modifier.id).toLowerCase();
      return {
        id,
        originalName,
        type,
        subtype,
        key: `${originalName}|${type}|${subtype}`,
      };
    };

    const normalizedModifiers = rawModifiers
      .map((m) => normalizeModifier(m))
      .filter(Boolean);

    const desiredModifierKeys = new Set(normalizedModifiers.map((x) => x.key));
    // const desiredModifierIds = new Set(
    //   normalizedModifiers.map((x) => x.id).filter(Boolean)
    // );
    const desiredModifierKeyById = new Map(
      normalizedModifiers.filter((x) => !!x.id).map((x) => [x.id, x.key]),
    );

    // Compare existing attributes with desired modifiers by name, title/type, and subtitle/subtype
    const existingModifierKeys = new Set(
      (targetExercise.attributes || []).map((attr) => keyForAttribute(attr)),
    );

    // Check if modifiers have changed
    const modifiersChanged =
      existingModifierKeys.size !== desiredModifierKeys.size ||
      ![...existingModifierKeys].every((key) => desiredModifierKeys.has(key));

    if (!modifiersChanged) {
      // No changes needed
      return;
    }

    // Remove only attributes that are no longer present in the desired modifiers.
    // Prefer matching by id when present in the payload, but keep key matching as a fallback.
    const currentAttributes = targetExercise.attributes || [];
    const toRemove = currentAttributes.filter((attr) => {
      const attrId = normalizeString(attr?.id).toLowerCase();
      const attrKey = keyForAttribute(attr);
      // If the payload provides ids, only keep the attribute if the id matches AND
      // it represents the same exact modifier (same key). Otherwise treat as changed.
      const desiredKeyForId = attrId
        ? desiredModifierKeyById.get(attrId)
        : null;
      const isDesiredById = desiredKeyForId
        ? desiredKeyForId === attrKey
        : false;
      const isDesiredByKey = desiredModifierKeys.has(attrKey);
      return !(isDesiredById || isDesiredByKey);
    });

    toRemove.forEach((attr) => {
      coach.deselectAttribute(targetExercise, attr, t);
    });

    // Re-read current attributes after removals (deselectAttribute mutates exercise.attributes).
    const remainingAttributes = targetExercise.attributes || [];

    // Apply modifiers to exactly match desired payload, but without recreating
    // Attribute objects/ids unnecessarily. This avoids dependency on
    // customPredefinedAttributes and ensures missing modifiers still apply.
    const EXCLUSIVE_TITLES = new Set([
      Attribute.AttributeTitles.equipment,
      Attribute.AttributeTitles.tempo,
      Attribute.AttributeTitles.rom,
      Attribute.AttributeTitles.stanceGrip,
    ]);

    // Apply exclusivity deterministically (last one wins for exclusive categories).
    const lastExclusiveByTitle = new Map();
    const nonExclusive = [];
    normalizedModifiers.forEach((m) => {
      if (m.type && EXCLUSIVE_TITLES.has(m.type)) {
        lastExclusiveByTitle.set(m.type, m);
      } else {
        nonExclusive.push(m);
      }
    });

    // Ensure each exact modifier appears at most once (last wins).
    const lastByKey = new Map();
    [...nonExclusive, ...Array.from(lastExclusiveByTitle.values())].forEach(
      (m) => lastByKey.set(m.key, m),
    );
    const desiredModifiersOrdered = Array.from(lastByKey.values());

    // Build reuse maps from existing attributes.
    const existingById = new Map();
    const existingByKey = new Map();
    remainingAttributes.forEach((attr) => {
      const id = normalizeString(attr?.id).toLowerCase();
      if (id) existingById.set(id, attr);
      const key = keyForAttribute(attr);
      if (!existingByKey.has(key)) existingByKey.set(key, []);
      existingByKey.get(key).push(attr);
    });

    const nextAttributes = [];
    desiredModifiersOrdered.forEach((m, idx) => {
      let attrToUse = null;

      if (m.id && existingById.has(m.id)) {
        const candidate = existingById.get(m.id);
        // Only reuse if it's the same exact modifier; otherwise treat as changed.
        if (candidate && keyForAttribute(candidate) === m.key) {
          attrToUse = candidate;
        }
      } else {
        const bucket = existingByKey.get(m.key) || [];
        if (bucket.length > 0) attrToUse = bucket.shift();
      }

      if (!attrToUse) {
        // If the payload provides an id (recently used), keep it stable.
        // Otherwise allow Attribute to generate one.
        attrToUse = new Attribute(
          undefined,
          m.originalName,
          m.type,
          m.subtype,
          idx,
          false,
          false,
          null,
          targetExercise,
        );
      } else {
        // Do NOT update attribute identity fields (name/title/subtitle).
        attrToUse.order = idx;
        attrToUse.exercise = targetExercise;
      }

      nextAttributes.push(attrToUse);
    });

    targetExercise.setAttributes(nextAttributes);

    // Preserve full desired name (including missing modifiers).
    if (targetExercise.name !== desiredName) {
      targetExercise.setProperty("name", desiredName);
    }

    // Update transactions
    if (this.client) {
      if (this.client?.avoidTransaction === false) {
        this.client.updateClientTransactionData(this.id.toLowerCase());
        this.client.startOrResetBatchTimer();
      }
    } else {
      if (this.coach) {
        this.coach.updateCoachTransactionBlocks(this.id.toLowerCase());
        this.coach.startOrResetBatchTimer();
      }
    }
  }

  checkIfSeriesWithData(targetWeek = null, protocolSlot = null) {
    // Get exercises: if targetWeek is null, check across all weeks; otherwise filter by week
    const weekExercises =
      targetWeek === null
        ? this.workouts.flatMap((w) => w?.exercises)
        : this.workouts
            .filter((w) => w?.week === targetWeek)
            .flatMap((w) => w?.exercises);

    // If a protocolSlot was passed (allow 0), filter by it; otherwise use all
    const list =
      protocolSlot !== null && protocolSlot !== undefined
        ? weekExercises.filter((ex) => ex?.protocolSlot === protocolSlot)
        : weekExercises;

    return list.some((ex) => ex?.hasSeriesWithData);
  }

  copyProtocol(slotExercise) {
    const copyProtocolOfExercise = (sourceSlotExercise) => {
      let selectedWorkoutSlotId = null;
      let selectedProtocolSlotId = null;

      // 1) Find slots for the next week
      const upcomingSlots = this.protocolsAndExercises.filter(
        (ex) =>
          ex.protocolSlot === sourceSlotExercise.protocolSlot &&
          ex.getWeek === sourceSlotExercise.getWeek + 1 &&
          ex.isSecondaryProtocol === sourceSlotExercise.isSecondaryProtocol,
      );

      //Detect if it's a second exercise that we need to add to a workout
      let addSecondaryExerciseToWorkout = false;
      let firstSlotInWorkout = null;
      if (sourceSlotExercise.isSecondaryProtocol) {
        const upcomingFirstSlotsInWorkout = this.protocolsAndExercises.filter(
          (ex) =>
            ex.protocolSlot === sourceSlotExercise.protocolSlot &&
            ex.getWeek === sourceSlotExercise.getWeek + 1 &&
            !ex.isSecondaryProtocol &&
            ex.workout,
        );

        //Detect when the first exercise is in a workout but there is no secondary yet
        addSecondaryExerciseToWorkout =
          upcomingSlots.filter((ex) => ex.workout).length === 0 &&
          upcomingFirstSlotsInWorkout.length > 0;
        firstSlotInWorkout = upcomingFirstSlotsInWorkout[0];
      }

      // 2) Update existing slots if any
      if (upcomingSlots.length > 0) {
        upcomingSlots.forEach((targetSlot) => {
          // Copy main properties
          [
            "name",
            "originalName",
            "notes",
            "protocolText",
            "isSecondaryProtocol",
            "link",
          ].forEach((prop) =>
            targetSlot.setProperty(prop, sourceSlotExercise[prop]),
          );
          // Reset series & attributes
          targetSlot.setProperty("series", []);
          targetSlot.setProperty("attributes", []);
          // Copy series & attributes
          sourceSlotExercise.series.forEach((s) => targetSlot.copySerie(s));
          sourceSlotExercise.attributes.forEach((a) =>
            targetSlot.copyAttribute(a),
          );

          // Track selection ID
          if (targetSlot.workout) {
            selectedWorkoutSlotId = targetSlot.id.toLowerCase();
            targetSlot.workout.setProperty("hasChanged", true);
          }

          if (targetSlot.block)
            selectedProtocolSlotId = targetSlot.id.toLowerCase();
        });

        if (!addSecondaryExerciseToWorkout) {
          return selectedWorkoutSlotId || selectedProtocolSlotId;
        }
      }

      // 3) No existing slot: create a new exercise
      const newExercise = new Exercise(
        undefined,
        sourceSlotExercise.name,
        sourceSlotExercise.originalName,
        sourceSlotExercise.notes,
        0,
        true,
        false,
        null,
        [],
        [],
        null,
        this,
        sourceSlotExercise.link,
      );
      newExercise.setProperty("protocolSlot", sourceSlotExercise.protocolSlot);
      newExercise.setProperty("protocolWeek", sourceSlotExercise.getWeek + 1);
      newExercise.setProperty("protocolText", sourceSlotExercise.protocolText);
      newExercise.setProperty(
        "isSecondaryProtocol",
        sourceSlotExercise.isSecondaryProtocol,
      );

      sourceSlotExercise.series.forEach((s) => newExercise.copySerie(s));
      sourceSlotExercise.attributes.forEach((a) =>
        newExercise.copyAttribute(a),
      );

      //Add the exercise to the workout
      if (addSecondaryExerciseToWorkout) {
        console.log("upcomingFirstSlot.workout if");

        // Insert secondary immediately after the primary slot’s order
        const primaryOrder = firstSlotInWorkout.order;
        const insertOrder = primaryOrder + 1;

        // Bump orders of existing exercises at or after the insertion point
        firstSlotInWorkout.workout.exercises.forEach((ex) => {
          if (ex.order >= insertOrder) {
            ex.order += 1;
          }
        });

        // Assign and insert the new secondary exercise
        newExercise.setProperty("block", null);
        newExercise.order = insertOrder;
        newExercise.workout = firstSlotInWorkout.workout;
        newExercise.workout?.setProperty("hasChanged", true);
        firstSlotInWorkout.workout.exercises.push(newExercise);
        return;
      } else {
        this.protocols.push(newExercise);
      }

      if (this.client) {
        this.client.updateClientTransactionData(this.id.toLowerCase());
        this.client.startOrResetBatchTimer();
      } else if (this.coach) {
        this.coach.updateCoachTransactionBlocks(this.id.toLowerCase());
        this.coach.startOrResetBatchTimer();
      }

      return newExercise.id.toLowerCase();
    };

    const selectedSlotId = copyProtocolOfExercise(slotExercise);
    this.selectedSlotId = selectedSlotId;

    const sourceSecondExercise = slotExercise.getSecondProtocol;

    if (sourceSecondExercise) {
      console.log("sourceSecondExercise executing copyProtocolOfExercise");
      copyProtocolOfExercise(sourceSecondExercise);
    } else {
      //Check if we need to delete the secondExercise from a workout or from protocols
      const upcomingSecondSlots = this.protocolsAndExercises.filter(
        (ex) =>
          ex.protocolSlot === slotExercise.protocolSlot &&
          ex.getWeek === slotExercise.getWeek + 1 &&
          ex.isSecondaryProtocol,
      );
      //Check if we need to delete the secondExercise from a workout or protocols
      if (upcomingSecondSlots.length > 0) {
        upcomingSecondSlots.forEach((targetSlot) => {
          if (targetSlot.workout) {
            const exArr = targetSlot.workout.exercises;
            const idx = exArr.findIndex((e) => e.id === targetSlot.id);
            if (idx > -1) {
              exArr.splice(idx, 1);

              targetSlot.workout.setProperty("hasChanged", true);
            }
          } else {
            const idx = this.protocols.findIndex((p) => p.id === targetSlot.id);
            if (idx > -1) {
              this.protocols.splice(idx, 1);
            }
          }
        });
      }
    }
  }

  copyWeekOfProtocols(week) {
    const protocolExercises = this.protocolsAndExercises.filter(
      (ex) => ex.getWeek === week && !ex.isSecondaryProtocol,
    );

    // Deduplicate by protocolSlot, preferring exercises that have workout defined
    const slotsMap = new Map();
    protocolExercises.forEach((ex) => {
      const slot = ex.protocolSlot;
      const existing = slotsMap.get(slot);
      // If no entry yet, or existing has no workout but this one does, replace
      if (!existing || (!existing.workout && ex.workout)) {
        slotsMap.set(slot, ex);
      }
    });

    // Execute copyProtocol for each selected exercise
    slotsMap.forEach((protocolExercise) => {
      this.copyProtocol(protocolExercise);
    });
  }

  deleteProtocol() {
    const slot = this.selectedSlot;

    if (!slot) return;

    //If deleting the primary, also delete the secondary
    if (!slot.isSecondaryProtocol && slot.getSecondProtocol) {
      const idx = this.protocols.findIndex(
        (ex) => ex.id === slot.getSecondProtocol.id,
      );
      if (idx >= 0) this.protocols.splice(idx, 1);
    }

    this.selectedSlotId = null;
    this.selectedProtocolSlot = slot.protocolSlot;
    this.selectedProtocolWeek = slot.protocolWeek;

    const idx = this.protocols.findIndex((ex) => ex.id === slot.id);
    if (idx >= 0) this.protocols.splice(idx, 1);

    if (this.client) {
      this.client.updateClientTransactionData(this.id.toLowerCase());
      this.client.startOrResetBatchTimer();
    } else if (this.coach) {
      this.coach.updateCoachTransactionBlocks(this.id.toLowerCase());
      this.coach.startOrResetBatchTimer();
    }
  }

  deleteExerciseAndSlot(exercise) {
    // 1) Remove matching workout exercises
    const exercisesToDelete = this.workouts
      .filter((w) => w.week === exercise.getWeek)
      .flatMap((w) =>
        w.exercises.filter(
          (e) =>
            e.protocolSlot === exercise.protocolSlot &&
            e.getWeek === exercise.getWeek &&
            (e.isSecondaryProtocol === exercise.isSecondaryProtocol ||
              !exercise.isSecondaryProtocol),
        ),
      );

    exercisesToDelete.forEach((eToDel) => {
      // Mark the workout as changed
      eToDel.workout.setProperty("hasChanged", true);
      // Remove the exercise from its workout
      const idx = eToDel.workout.exercises.findIndex(
        (ex) => ex.id === eToDel.id,
      );
      if (idx > -1) {
        eToDel.workout.exercises.splice(idx, 1);
      }
    });

    // 2) Update or remove protocol slot definitions
    const slotExercisesToDelete = this.protocols.filter(
      (slot) =>
        slot.protocolWeek === exercise.getWeek &&
        slot.protocolSlot === exercise.protocolSlot &&
        (slot.isSecondaryProtocol === exercise.isSecondaryProtocol ||
          !exercise.isSecondaryProtocol),
    );

    slotExercisesToDelete.forEach((slot) => {
      const idx = this.protocols.findIndex((p) => p.id === slot.id);
      if (idx > -1) {
        this.protocols.splice(idx, 1);
      }
    });

    // 3) Persist changes
    if (this.client) {
      this.client.updateClientTransactionData(this.id.toLowerCase());
      this.client.startOrResetBatchTimer();
    } else if (this.coach) {
      this.coach.updateCoachTransactionBlocks(this.id.toLowerCase());
      this.coach.startOrResetBatchTimer();
    }
  }

  /**
   * Delete a workout exercise using the same logic as ExerciseCard.js
   * This method handles:
   * - Copying exercise to block.protocols if it has a protocolSlot
   * - Removing exercise from workout
   * - Persisting changes to Firestore
   * - Handling protocol exercises (no workout)
   *
   * @param {Exercise} exercise - The exercise to delete
   */
  deleteWorkoutExercise(exercise) {
    if (!exercise) return;

    if (exercise.workout) {
      // Copy to block.protocols before deleting (only if exercise has protocolSlot)
      if (exercise.protocolSlot != null) {
        exercise.copyExerciseToBlockProtocols?.();
      }

      const workout = exercise.workout;
      const idx = Array.isArray(workout?.exercises)
        ? workout.exercises.findIndex((ex) => ex?.id === exercise.id)
        : -1;

      if (idx > -1) {
        workout.setProperty?.("hasChanged", true);
        workout.exercises.splice(idx, 1);
      }
    } else {
      // Protocol exercise (no workout) - delete from protocols. Should not happen
      this.setProperty("selectedSlotId", exercise.id.toLowerCase());
      this.deleteProtocol();
    }

    // Persist changes to Firestore
    if (this.client) {
      this.client.updateClientTransactionData(String(this.id).toLowerCase());
      this.client.startOrResetBatchTimer();
    } else if (this.coach) {
      this.coach.updateCoachTransactionBlocks(String(this.id).toLowerCase());
      this.coach.startOrResetBatchTimer();
    }
  }

  getProtocolSeriesShown(protocolTitle) {
    return this.protocolSeriesShown.includes(protocolTitle);
  }

  toggleProtocolSeriesShown(protocolTitle) {
    const idx = this.protocolSeriesShown.indexOf(protocolTitle);
    if (idx >= 0) {
      // already in the array → remove it
      this.protocolSeriesShown.splice(idx, 1);
    } else {
      // not in the array → add it
      this.protocolSeriesShown.push(protocolTitle);
    }
  }

  toggleAllProtocols() {
    //Get the different protocolSlot of all protocols and workout exercises
    // collect protocolSlot values and ignore falsy values
    const allProtocols = this.protocolsAndExercises
      .map((ex) => ex.protocolSlot)
      .filter((s) => s);

    // avoid duplicates
    const uniqueProtocols = [...new Set(allProtocols)];

    uniqueProtocols.forEach(
      (p) =>
        // if not already, add it to the array protocolSeriesShown
        this.protocolSeriesShown.includes(p) ||
        this.protocolSeriesShown.push(p),
    );
    return uniqueProtocols;
  }

  createSlotFromWorkoutExercise(exercise) {
    if (!exercise.protocolSlot) return;

    // Remove any existing slot for this protocolSlot
    const existingIdx = this.protocols.findIndex(
      (p) =>
        p.protocolSlot === exercise.protocolSlot &&
        p.isSecondaryProtocol === exercise.isSecondaryProtocol &&
        p.protocolWeek === exercise.getWeek,
    );
    if (existingIdx > -1) {
      console.log(
        "removing protocol in createSlotFromWorkoutExericise",
        this.protocols[existingIdx].name,
      );
      this.protocols.splice(existingIdx, 1);
    }
    // Create new protocol exercise
    const newSlot = new Exercise(
      undefined,
      exercise.name,
      exercise.originalName,
      exercise.notes,
      0,
      undefined,
      false,
      null,
      [],
      [],
      undefined,
      this,
      exercise.link,
    );

    newSlot.setProperty("isSecondaryProtocol", exercise.isSecondaryProtocol);
    newSlot.setProperty("protocolSlot", exercise.protocolSlot);
    newSlot.setProperty("protocolText", exercise.protocolText ?? "");
    newSlot.setProperty("protocolWeek", exercise.workout.week);

    // Copy series and attributes
    exercise.series.forEach((serie) => newSlot.copySerie(serie));
    exercise.attributes.forEach((attr) => newSlot.copyAttribute(attr));

    this.protocols.push(newSlot);

    return newSlot;
  }

  // #endregion

  // #region /* HIDDEN WEEKS & WEEK NAMES */
  setWeekName(weekNumber, name) {
    if (name && name.trim()) {
      // store only non‐empty names
      this.weekNames[weekNumber] = name.trim();
    } else {
      // remove it if they cleared the name
      delete this.weekNames[weekNumber];
    }
    // persist weekNamesMap to Firestore…

    if (this.client) {
      if (this.client?.avoidTransaction === false) {
        this.client.updateClientTransactionData(this.id.toLowerCase());
        this.client.startOrResetBatchTimer();
      }
    } else {
      if (this.coach) {
        this.coach.updateCoachTransactionBlocks(this.id.toLowerCase());
        this.coach.startOrResetBatchTimer();
      }
    }
  }

  getWeekName(weekNumber) {
    return this.weekNames[weekNumber] ?? null;
  }

  deleteWeekName(weekNumber) {
    if (this.weekNames.hasOwnProperty(weekNumber)) {
      delete this.weekNames[weekNumber];

      if (this.client) {
        if (this.client?.avoidTransaction === false) {
          this.client.updateClientTransactionData(this.id.toLowerCase());
          this.client.startOrResetBatchTimer();
        }
      } else {
        if (this.coach) {
          this.coach.updateCoachTransactionBlocks(this.id.toLowerCase());
          this.coach.startOrResetBatchTimer();
        }
      }
    }
  }

  toggleHiddenWeek(weekNumber) {
    if (this.hiddenWeeks.includes(weekNumber)) {
      this.hiddenWeeks = this.hiddenWeeks.filter((w) => w !== weekNumber);
    } else {
      this.hiddenWeeks = [...this.hiddenWeeks, weekNumber];
    }
  }

  isWeekHidden(weekNumber) {
    return this.hiddenWeeks.includes(weekNumber);
  }
  // #endregion

  // #region /* HELPERS */
  setIsSelected(value) {
    this.isSelected = value;
  }
  setIsShown(value) {
    this.isShown = value;
  }

  get isActive() {
    const activeBlock = this.client?.activeBlock?.();
    return Boolean(activeBlock && activeBlock.id === this.id);
  }

  get selectedWorkout() {
    const weekWorkouts = this.workouts
      .filter((workout) => workout.week === this.selectedWeek) // Filter workouts for the specific week
      .sort((a, b) => a.order - b.order); // Sort workouts by their 'order' property

    // Find the first workout of the selected week
    const firstWorkoutOfWeek = weekWorkouts[0];

    // Find the selected workout of the selected week
    const selectedWorkoutOfWeek = this.workouts.find(
      (workout) =>
        workout.week === this.selectedWeek && workout.isSelected === true,
    );

    // If no workout is selected for the week, select the first workout of the week
    if (!selectedWorkoutOfWeek && firstWorkoutOfWeek) {
      this.setSelectedWorkout(firstWorkoutOfWeek);
      return firstWorkoutOfWeek;
    }

    // Return the selected workout or the first workout as fallback
    return selectedWorkoutOfWeek || firstWorkoutOfWeek;
  }

  setSelectedWorkout(selectedWorkout) {
    this.workouts.forEach((workout) => {
      if (selectedWorkout && workout.id === selectedWorkout.id) {
        workout.setIsSelected(true);
      } else {
        workout.setIsSelected(false);
      }
    });
  }

  setBlockViewEditingWorkoutId(workoutId) {
    // Simple O(1) assignment - no array searching needed
    this.blockViewEditingWorkoutId = workoutId
      ? String(workoutId).toLowerCase()
      : null;
  }

  clearBlockViewEditingWorkoutId() {
    this.blockViewEditingWorkoutId = null;
  }

  get getWeeks() {
    return this.workouts.length > 0
      ? Math.max(...this.workouts.map((workout) => workout.week))
      : 1;
  }

  currentWeek(startDate = new Date()) {
    const startOfThisWeek = startOfWeek(startDate);
    const endOfThisWeek = endOfWeek(startDate);
    const workouts = this.workouts ?? [];

    // 1) Check if there are any workouts this week
    const thisWeekWorkouts = workouts.filter((w) => {
      if (!(w.date instanceof Date)) return false;
      const date = w.date;
      return date >= startOfThisWeek && date <= endOfThisWeek;
    });

    if (thisWeekWorkouts.length > 0) {
      // Return the highest week number among workouts this week
      return Math.max(...thisWeekWorkouts.map((w) => w.week));
    }

    // 2) No workouts this week, find workouts in the past
    const pastWorkouts = workouts.filter((w) => {
      if (!(w.date instanceof Date)) return false;
      const date = w.date;
      return date < startOfThisWeek;
    });

    if (pastWorkouts.length === 0) {
      // No past workouts, check for future workouts
      const futureWorkouts = workouts.filter((w) => {
        if (!(w.date instanceof Date)) return false;
        const date = w.date;
        return date > endOfThisWeek;
      });

      if (futureWorkouts.length > 0) {
        // Return the minimum week from future workouts
        return Math.min(...futureWorkouts.map((w) => w.week));
      }

      // No dated workouts at all, check if calendarStartDate exists
      if (this.calendarStartDate) {
        const calendarStart =
          this.calendarStartDate instanceof Date
            ? this.calendarStartDate
            : new Date(this.calendarStartDate);

        const calendarStartWeek = startOfWeek(calendarStart);

        // Calculate weeks elapsed since calendar start
        const weeksDiff = Math.floor(
          (startOfThisWeek - calendarStartWeek) / (7 * 24 * 60 * 60 * 1000),
        );

        // Current week is 1 + weeks elapsed (week 1 starts at calendarStartDate)
        const estimatedWeek = 1 + weeksDiff;

        // Ensure we don't exceed totalWeeks
        return Math.min(Math.max(1, estimatedWeek), this.totalWeeks);
      }

      // Fallback: return 1 or highest week among all workouts
      const allWeeks = workouts
        .filter((w) => typeof w.week === "number")
        .map((w) => w.week);
      return allWeeks.length > 0 ? Math.max(...allWeeks) : 1;
    }

    // 3) Find the most recent past workout
    const mostRecentPastWorkout = pastWorkouts.reduce((latest, w) => {
      return !latest || w.date > latest.date ? w : latest;
    }, null);

    if (!mostRecentPastWorkout) {
      return 1;
    }

    // 4) Find the highest week number among past workouts
    const highestPastWeek = Math.max(...pastWorkouts.map((w) => w.week));

    // 5) Calculate weeks elapsed since the most recent past workout
    const mostRecentDate = mostRecentPastWorkout.date;
    const mostRecentWeekStart = startOfWeek(mostRecentDate);
    const weeksDiff = Math.floor(
      (startOfThisWeek - mostRecentWeekStart) / (7 * 24 * 60 * 60 * 1000),
    );

    // 6) Estimate current week: highest past week + weeks elapsed
    const estimatedWeek = highestPastWeek + weeksDiff;

    // 7) Ensure we don't exceed totalWeeks
    return Math.min(estimatedWeek, this.totalWeeks);
  }

  get selectedWeekWorkouts() {
    if (this.selectedWeek) {
      return this.workouts
        .filter((workout) => workout.week === this.selectedWeek)
        .sort((a, b) => a.order - b.order);
    } else {
      return [];
    }
  }

  get firstWeekWorkouts() {
    return this.workouts
      .filter((workout) => workout.week === 1)
      .sort((a, b) => a.order - b.order);
  }

  setProperty(propertyName, value) {
    if (Object.hasOwnProperty.call(this, propertyName)) {
      this[propertyName] = value;
    } else {
      console.error(`Property ${propertyName} does not exist.`);
    }
  }

  getProperty(propertyName) {
    if (Object.hasOwnProperty.call(this, propertyName)) {
      return this[propertyName];
    } else {
      console.error(`Property ${propertyName} does not exist.`);
      return undefined;
    }
  }
  // #endregion
}

export default Block;
